import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndustry, faArrowLeft, faEye, faFileAlt, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import API_BASE_URL from '../../../config/api';

// Debug: Log API base URL
console.log('🔧 Cement Testing Test Results API - Base URL:', API_BASE_URL);

const CementTestingTestResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testData, setTestData] = useState({});
  const [cementTestRequestId, setCementTestRequestId] = useState(null);
  const [loadingObservationSheet, setLoadingObservationSheet] = useState(false);
  const [loadingTestReport, setLoadingTestReport] = useState(false);

  const testConfigurations = {
    fineness: {
      name: 'Specific Gravity of Cement',
      description: 'Determination of Specific Gravity of Cement',
      color: 'var(--vitrag-primary)'
    },
    consistency: {
      name: 'Soundness of Cement',
      description: 'Determination of Soundness of Cement',
      color: 'var(--vitrag-success)'
    },
    settingTime: {
      name: 'Fineness of Cement',
      description: 'Determination of Fineness of Cement by Dry Sieving',
      color: 'var(--vitrag-warning)'
    },
    soundness: {
      name: 'Consistency & Setting Time',
      description: 'Standard Consistency Test & Initial and Final Setting Time',
      color: 'var(--vitrag-danger)'
    },
    compressiveStrength: {
      name: 'Compressive Strength',
      description: 'Determination of Compressive Strength of Cement/Mortar Cube',
      color: 'var(--vitrag-info)'
    }
  };

  // Map testIds to their display order (1-5)
  const testOrderMap = {
    fineness: 1,          // Test 1: Specific Gravity
    consistency: 2,        // Test 2: Soundness
    settingTime: 3,       // Test 3: Fineness
    soundness: 4,         // Test 4: Consistency & Setting Time
    compressiveStrength: 5 // Test 5: Compressive Strength
  };

  useEffect(() => {
    console.log('🔍 Location state:', location.state);
    if (location.state && location.state.selectedTests) {
      // Sort selectedTests based on the new sequence order
      const sortedTests = [...location.state.selectedTests].sort((a, b) => {
        const orderA = testOrderMap[a] || 999;
        const orderB = testOrderMap[b] || 999;
        return orderA - orderB;
      });
      setSelectedTests(sortedTests);
      
      // Get cement_test_request_id from location state
      if (location.state.cement_test_request_id) {
        console.log('✅ Setting cement_test_request_id:', location.state.cement_test_request_id);
        setCementTestRequestId(location.state.cement_test_request_id);
      } else {
        console.warn('⚠️ No cement_test_request_id in location state');
      }
      
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        const mock = {};
        sortedTests.forEach((t) => {
          mock[t] = {
            status: 'completed',
            completedDate: new Date().toLocaleDateString(),
            observations: 'Test observations completed successfully'
          };
        });
        setTestData(mock);
      }, 1200);
    } else {
      navigate('/cement-testing-form');
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 768px) {
        .test-result-card { margin-bottom: 1rem; }
        .test-result-card .card-body { padding: 1rem; }
        .test-result-card .btn { font-size: 0.875rem; padding: 0.5rem 1rem; }
      }
      .test-result-card { transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out; }
      .test-result-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important; }
      .btn-observation { transition: all 0.2s ease-in-out; }
      .btn-observation:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,123,255,0.3); }
      .btn-report { transition: all 0.2s ease-in-out; }
      .btn-report:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,123,255,0.3); }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const handleViewObservationSheet = async (testId) => {
    console.log('🔍 View Observation Sheet clicked:', { testId, cementTestRequestId });
    if (!cementTestRequestId) {
      console.error('❌ No cementTestRequestId available');
      alert('Test request ID not found. Please save the test data first.');
      return;
    }
    
    // Map testId to test type number (1-5)
    // Based on CementTestingForm.js testTypes:
    // Test 1: fineness (Specific Gravity)
    // Test 2: consistency (Soundness)  
    // Test 3: settingTime (Fineness)
    // Test 4: soundness (Setting Time)
    // Test 5: compressiveStrength (Compressive Strength)
    const testTypeMap = {
      fineness: '1',          // Test 1: Specific Gravity
      consistency: '2',        // Test 2: Soundness
      settingTime: '3',       // Test 3: Fineness
      soundness: '4',         // Test 4: Setting Time
      compressiveStrength: '5' // Test 5: Compressive Strength
    };
    
    const testType = testTypeMap[testId];
    if (!testType) {
      alert('Invalid test type.');
      return;
    }
    
    try {
      setLoadingObservationSheet(true);
      const apiUrl = `${API_BASE_URL}/cement-testing/${cementTestRequestId}/observation-sheet/${testType}`;
      console.log('🔍 Fetching observation sheet from:', apiUrl);
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
        console.error('❌ API Error:', errorData);
        throw new Error(errorData.error || `Failed to fetch observation sheet data (${response.status})`);
      }
      
      const result = await response.json();
      console.log('✅ Observation sheet response:', result);
      
      const observationSheetUrl = result.observation_sheet_url;
      if (!observationSheetUrl) {
        throw new Error('No observation sheet URL returned from server');
      }
      
      // Make sure URL is absolute
      const fullUrl = observationSheetUrl.startsWith('http') 
        ? observationSheetUrl 
        : `${window.location.origin}${observationSheetUrl.startsWith('/') ? '' : '/'}${observationSheetUrl}`;
      
      console.log('🔗 Opening observation sheet URL:', fullUrl);
      window.open(fullUrl, '_blank');
    } catch (error) {
      console.error('❌ Error fetching observation sheet:', error);
      alert(`Error loading observation sheet: ${error.message}`);
    } finally {
      setLoadingObservationSheet(false);
    }
  };

  const handleViewTestReport = async () => {
    console.log('🔍 View Test Report clicked:', { cementTestRequestId });
    if (!cementTestRequestId) {
      console.error('❌ No cementTestRequestId available');
      alert('Test request ID not found. Please save the test data first.');
      return;
    }
    
    try {
      setLoadingTestReport(true);
      const apiUrl = `${API_BASE_URL}/cement-testing/${cementTestRequestId}/test-report`;
      console.log('🔍 Fetching test report from:', apiUrl);
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
        console.error('❌ API Error:', errorData);
        throw new Error(errorData.error || `Failed to fetch test report data (${response.status})`);
      }
      
      const result = await response.json();
      console.log('✅ Test report response:', result);
      
      const testReportUrl = result.test_report_url;
      if (!testReportUrl) {
        throw new Error('No test report URL returned from server');
      }
      
      // Make sure URL is absolute
      const fullUrl = testReportUrl.startsWith('http') 
        ? testReportUrl 
        : `${window.location.origin}${testReportUrl.startsWith('/') ? '' : '/'}${testReportUrl}`;
      
      console.log('🔗 Opening test report URL:', fullUrl);
      window.open(fullUrl, '_blank');
    } catch (error) {
      console.error('❌ Error fetching test report:', error);
      alert(`Error loading test report: ${error.message}`);
    } finally {
      setLoadingTestReport(false);
    }
  };

  const handleBackToForm = () => {
    navigate('/cement-testing-form');
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#1C2333' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" className="mb-3" />
          <h5 className="text-white">Processing Test Data...</h5>
          <p className="text-muted">Please wait while we prepare your test results</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--vitrag-main-bg)' }}>
      <div className="py-4" style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={8}>
              <div className="d-flex align-items-center flex-wrap">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center me-3 mb-2 mb-md-0"
                  style={{ width: '60px', height: '60px', backgroundColor: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.3)' }}
                >
                  <FontAwesomeIcon icon={faIndustry} size="2x" className="text-white" />
                </div>
                <div>
                  <h2 className="mb-1 text-white fw-bold">Cement Testing - Results</h2>
                  <p className="mb-0 text-white-50">Test data has been saved successfully</p>
                </div>
              </div>
            </Col>
            <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0"></Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        <Row className="mb-4">
          <Col>
            <Alert variant="success" className="border-0 shadow-sm alert-vitrag" style={{ backgroundColor: 'var(--vitrag-alert-success-bg)', borderColor: 'var(--vitrag-alert-success-border)', color: 'var(--vitrag-alert-success-text)' }}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="me-3" style={{ color: 'var(--vitrag-success)' }} size="lg" />
                <div>
                  <h5 className="mb-1" style={{ color: 'var(--vitrag-alert-success-text)' }}>Test Data Saved Successfully!</h5>
                  <p className="mb-0" style={{ color: 'var(--vitrag-alert-success-text)' }}>All selected tests have been completed and data has been saved to the system.</p>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <Card className="border-0 shadow-sm test-result-card card-vitrag">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary-bg)', borderBottom: '1px solid var(--vitrag-card-border)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-primary-text)' }}>
                  <FontAwesomeIcon icon={faIndustry} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                  Selected Tests ({selectedTests.length})
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                {selectedTests.map((testId, index) => {
                  const cfg = testConfigurations[testId];
                  if (!cfg) return null;
                  return (
                    <div key={testId} className={`p-4 ${index !== selectedTests.length - 1 ? 'border-bottom' : ''}`} style={{ backgroundColor: index % 2 === 0 ? 'var(--vitrag-card-bg)' : 'var(--vitrag-secondary-bg)' }}>
                      <Row className="align-items-center">
                        <Col xs={12} md={8}>
                          <div className="d-flex align-items-center flex-wrap">
                            <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3 mb-2 mb-md-0" style={{ width: '50px', height: '50px', backgroundColor: cfg.color + '20', border: `2px solid ${cfg.color}` }}>
                              <FontAwesomeIcon icon={faIndustry} style={{ color: cfg.color }} />
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold" style={{ color: 'var(--vitrag-primary-text)' }}>{cfg.name}</h6>
                              <p className="mb-0 small" style={{ color: 'var(--vitrag-secondary-text)' }}>{cfg.description}</p>
                              <div className="d-flex align-items-center mt-1 flex-wrap">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" style={{ color: 'var(--vitrag-success)' }} size="sm" />
                                <span className="small fw-medium" style={{ color: 'var(--vitrag-success)' }}>Completed</span>
                                <span className="small ms-2" style={{ color: 'var(--vitrag-secondary-text)' }}>{testData[testId]?.completedDate}</span>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewObservationSheet(testId)}
                            disabled={loadingObservationSheet || !cementTestRequestId}
                            className="d-flex align-items-center w-100 w-md-auto justify-content-center btn-observation"
                            style={{ borderColor: 'var(--vitrag-gold)', color: 'var(--vitrag-gold)', backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => { if (!e.target.disabled) { e.target.style.backgroundColor = 'var(--vitrag-gold)'; e.target.style.color = 'var(--vitrag-primary-text)'; } }}
                            onMouseLeave={(e) => { if (!e.target.disabled) { e.target.style.backgroundColor = 'transparent'; e.target.style.color = 'var(--vitrag-gold)'; } }}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            {loadingObservationSheet ? 'Loading...' : 'View Observation Sheet'}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card className="border-0 shadow-sm card-vitrag">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary-bg)', borderBottom: '1px solid var(--vitrag-card-border)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-primary-text)' }}>
                  <FontAwesomeIcon icon={faFileAlt} className="me-2" style={{ color: 'var(--vitrag-info)' }} />
                  Comprehensive Test Report
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col xs={12} md={8}>
                    <h6 className="mb-2 fw-bold" style={{ color: 'var(--vitrag-primary-text)' }}>Generate Complete Test Report</h6>
                    <p className="mb-0" style={{ color: 'var(--vitrag-secondary-text)' }}>View a comprehensive report containing all test results, observations, and analysis for the selected tests.</p>
                  </Col>
                  <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0">
                    <Button 
                      variant="primary" 
                      size="lg" 
                      onClick={handleViewTestReport} 
                      disabled={loadingTestReport || !cementTestRequestId}
                      className="d-flex align-items-center w-100 w-md-auto justify-content-center btn-report btn-vitrag-primary"
                    >
                      <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                      {loadingTestReport ? 'Loading...' : 'View Test Report'}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col className="text-center">
            <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3">
              <Button
                variant="outline-secondary"
                onClick={handleBackToForm}
                className="d-flex align-items-center"
                style={{ minWidth: '150px', borderColor: 'var(--vitrag-secondary)', color: 'var(--vitrag-secondary)', backgroundColor: 'transparent' }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--vitrag-secondary)'; e.target.style.color = 'var(--vitrag-primary-text)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = 'var(--vitrag-secondary)'; }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Form
              </Button>
              <Button variant="success" onClick={() => navigate('/other-services-dashboard')} className="d-flex align-items-center btn-vitrag-primary" style={{ minWidth: '150px' }}>
                <FontAwesomeIcon icon={faIndustry} className="me-2" />
                Go to Dashboard
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CementTestingTestResults;


